import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { assetService } from '../../../services/assetService';
import { assetArchitectureService } from '../../../services/assetArchitectureService';
import projectAssetService from '../../../services/projectAssetService';
import ProjectAssetDrawer from './ProjectAssetDrawer';
import ProjectAssetTreeView from './ProjectAssetTreeView';
import MasterAssetSelectionDrawer from './MasterAssetSelectionDrawer';

// Brand Colors (kept for reference if needed, though mostly handled in components)
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  accent: "#A3CD39",
  dark: "#007F66",
};

interface ProjectAssetManagementProps {
  project: any;
  onEquipmentUpdate?: () => void;
}

interface ManageAssetOption {
  masterAssetId: string;
  name: string;
  description?: string;
  isSelected: boolean;
  projectAssetId?: string | null;
  equipmentCount: number;
}

/** Resolve Mongo id for the project's project type (e.g. Rooftop vs Ground-mount master catalog). */
function getProjectTypeId(project: any): string | undefined {
  if (!project) return undefined;
  const pt = project.projectTypeId;
  if (pt == null) return undefined;
  if (typeof pt === 'string' && pt.trim()) return pt.trim();
  const id = pt._id ?? pt.id;
  if (id != null) return String(id);
  return undefined;
}

export default function ProjectAssetManagement({ project, onEquipmentUpdate }: ProjectAssetManagementProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  // Available Assets (Master Assets)
  const [availableAssets, setAvailableAssets] = useState<ManageAssetOption[]>([]);

  // Checkpoint Management Drawer
  const [openProjectAsset, setOpenProjectAsset] = useState(false);
  const [selectedAssetForCheckpoints, setSelectedAssetForCheckpoints] = useState<any>(null);

  // Master Asset Selection Drawer
  const [openMasterDrawer, setOpenMasterDrawer] = useState(false);
  const [existingProjectAssets, setExistingProjectAssets] = useState<any[]>([]);


  // Fetch Existing Project Assets for the Project Assets strip.
  // Uses the dedicated `/project-assets/with-checkpoints` endpoint so each asset
  // arrives with its own `equipmentCount` and checkpoint counts — keeping this
  // section's data independent of the Site Equipment Network's equipment queries.
  const fetchProjectAssets = async () => {
    if (!project?._id && !project?.id) return;
    try {
      const assets = await projectAssetService.getProjectAssetsWithDetails(project._id || project.id);
      setExistingProjectAssets(assets);
    } catch (error) {
      console.error("Failed to fetch project assets", error);
    }
  };

  // Load active master assets for this project's project type only (e.g. Rooftop → rooftop catalog).
  const loadAvailableAssets = useCallback(async () => {
    try {
      const projectTypeId = getProjectTypeId(project);
      console.log(
        'ProjectAssetManagement: Loading active assets',
        projectTypeId ? `for projectTypeId=${projectTypeId}` : '(no project type — all active assets)'
      );

      // Fetch active assets with pagination handling since API limits to 100
      let allAssets: any[] = [];
      let page = 1;
      let hasMore = true;
      const LIMIT = 100;

      while (hasMore) {
        const response = await assetService.getAssets({
          status: 'active',
          ...(projectTypeId ? { projectTypeId } : {}),
          limit: LIMIT,
          page: page
        });

        const assets = response.assets || [];
        allAssets = [...allAssets, ...assets];

        // Check if we have more pages
        // Support different API response structures for total count
        const total = response.pagination?.total || (response as any).total || (response as any).totalDocs || 0;

        if (allAssets.length >= total || assets.length < LIMIT) {
          hasMore = false;
        } else {
          page++;
        }
      }

      console.log('ProjectAssetManagement: Total assets loaded:', allAssets.length);

      const options: ManageAssetOption[] = allAssets.map((asset: any) => ({
        masterAssetId: asset._id || asset.id,
        name: asset.name,
        description: asset.description,
        isSelected: false, // Not used in new view
        projectAssetId: null, // Not used in new view
        equipmentCount: 0
      }));

      setAvailableAssets(options);
    } catch (error) {
      console.error('Failed to load master assets:', error);
      toast.error('Failed to load available assets');
    }
  }, [project]);

  useEffect(() => {
    if (project) {
      loadAvailableAssets();
      fetchProjectAssets();
    }
  }, [project, loadAvailableAssets]);

  // Refresh project assets when equipment is updated (includes asset removal)
  const handleEquipmentUpdate = useCallback(() => {
    // Call the parent's onEquipmentUpdate if provided
    if (onEquipmentUpdate) {
      onEquipmentUpdate();
    }
    // Also refresh project assets to reflect any changes (like removals)
    fetchProjectAssets();
  }, [onEquipmentUpdate]);

  const handleOpenCheckpoints = async (node: any) => {
    // Node comes from ProjectAssetTreeView (Equipment node or Asset Card)
    // Equipment has projectAssetId which references ProjectAsset
    // ProjectAsset has masterAssetId which references Master Asset

    // Allow if we have projectAssetId OR masterAssetId
    if (!node.projectAssetId && !node.masterAssetId) {
      toast.error('Cannot manage checkpoints: Asset ID missing');
      return;
    }

    // If we only have masterAssetId (New Asset scenario from available list)
    if (node.masterAssetId && !node.projectAssetId) {
      setSelectedAssetForCheckpoints({
        name: node.name || node.assetType || 'New Asset',
        masterAssetId: node.masterAssetId,
      });
      setOpenProjectAsset(true);
      return;
    }

    // Fetch the ProjectAsset to get masterAssetId
    try {
      const projectAsset = await projectAssetService.getProjectAssetById(node.projectAssetId);

      setSelectedAssetForCheckpoints({
        _id: node.projectAssetId,
        projectAssetId: node.projectAssetId,
        name: node.assetType,
        masterAssetId: projectAsset.masterAssetId, // Pass masterAssetId for fetching master checkpoints
      });
      setOpenProjectAsset(true);
    } catch (error) {
      console.error('Failed to fetch project asset:', error);
      toast.error('Failed to load asset information');
    }
  };

  return (
    <div className="h-full">
      {/* New Tree View Component */}
      <ProjectAssetTreeView
        project={project}
        onEquipmentUpdate={handleEquipmentUpdate}
        onOpenCheckpoints={handleOpenCheckpoints}
        availableAssets={availableAssets}
        onAddMasterAsset={() => setOpenMasterDrawer(true)}
        projectAssets={existingProjectAssets}
      />

      {/* Project Asset Drawer (for managing asset checkpoints) */}
      {openProjectAsset && selectedAssetForCheckpoints && (
        <ProjectAssetDrawer
          open={openProjectAsset}
          onClose={() => {
            setOpenProjectAsset(false);
            setSelectedAssetForCheckpoints(null);
          }}
          assetName={selectedAssetForCheckpoints.name || 'Asset'}
          projectId={project._id || project.id}
          masterAssetId={selectedAssetForCheckpoints.masterAssetId || ""}
          projectStartDate={project.startDate}
        />
      )}


      {/* Master Asset Selection Drawer */}
      <MasterAssetSelectionDrawer
        open={openMasterDrawer}
        onClose={() => setOpenMasterDrawer(false)}
        availableAssets={availableAssets}
        existingProjectAssets={existingProjectAssets}
        onBulkAdd={async (assets) => {
          try {
            setLoading(true);
            const projectId = project._id || project.id;

            // Sequential creation to ensure order and avoid race conditions
            for (const asset of assets) {
              await projectAssetService.createOrGetProjectAsset(
                projectId,
                asset.masterAssetId
              );
            }

            toast.success(`Successfully added ${assets.length} assets to project`);

            // Close the drawer after successful bulk add
            setOpenMasterDrawer(false);

            // Refresh logic - handleEquipmentUpdate will refresh both equipment and project assets
            handleEquipmentUpdate();

          } catch (error) {
            console.error("Bulk add failed:", error);
            toast.error("Failed to add some assets");
          } finally {
            setLoading(false);
          }
        }}
        onSelectAsset={(asset) => {
          // Open the Project Asset Drawer for this master asset
          handleOpenCheckpoints({
            masterAssetId: asset.masterAssetId,
            name: asset.name,
            assetType: asset.name
          });
        }}
      />
    </div>
  );
}
